import { API } from "./apiConstants";
import ApiError from "./apiErrorModel";
import { getEncKeyHeader } from "../../utils/customerToken";

/*
    Fetch based API instance (axios has been removed from the app).
    get/post/put resolve with { data, status } and reject with an ApiError
    (status, response, type) so callers can keep using try/catch and mapError.
*/
const POST_HEADERS = {
  GlympseApiKey: "wuUthAkJaE283Ev8H8FOwC4VNda46nvF",
  GoogleCaptchaAuthentication: "tgAeRolDZQBxjZqrKKyYneldHExPUjDt",
  WeatherAPIAuthentication: "9F1E8E334CEE9C9D8C35B4647159A",
  ProductAPIAuthentication: "B6RCJ82fBusTXXTeBixrNIwjrJzeBIVT",
  YextAPIAuthentication: "F9C224A59996FC3B22F55FA5786E4",
  Bearer: "3rvf5hsk2uzxnkiwffaeg7t9ez31axxa",
};

const buildUrl = (baseURL, endpoint, params) => {
  const isAbsolute = /^https?:\/\//i.test(endpoint);
  let url = isAbsolute
    ? endpoint
    : `${(baseURL || "").replace(/\/+$/, "")}/${endpoint.replace(/^\/+/, "")}`;

  if (params && typeof params === "object") {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) query.append(key, value);
    });
    const queryString = query.toString();
    if (queryString) url += `${url.includes("?") ? "&" : "?"}${queryString}`;
  }
  return url;
};

const parseBody = async (res) => {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }
  return res.text();
};

function createApiInstance(baseURL, isAuth, settings = {}) {
  const timeout = settings.timeout || API.DEFAULT_TIMEOUT;
  const controllers = new Set();

  async function request({ method, url, params, data, mapResponse, mapError }) {
    const controller = new AbortController();
    controllers.add(controller);
    const timer = timeout
      ? setTimeout(() => controller.abort("timeout"), timeout)
      : null;

    try {
      const headers = {};
      const options = {
        method,
        headers,
        signal: controller.signal,
        credentials: isAuth ? "include" : "same-origin",
      };

      if (method !== API.METHOD.GET) {
        Object.assign(headers, POST_HEADERS, {
          "Content-Type": "application/json",
        });
        const encKey = getEncKeyHeader();
        if (encKey) headers.EncKey = encKey;
        options.body = typeof data === "string" ? data : JSON.stringify(data);
      }

      let res;
      try {
        res = await fetch(buildUrl(baseURL, url, params), options);
      } catch (e) {
        const cancelled = e?.name === "AbortError";
        throw new ApiError({
          type: cancelled ? API.ERROR_TYPE.CANCELLED : API.ERROR_TYPE.FAILED,
          genericMessage: e?.message,
          innerError: e,
        });
      }

      const body = await parseBody(res).catch(() => null);
      if (!res.ok) {
        throw new ApiError({
          type: API.ERROR_TYPE.NOT_OK,
          status: res.status,
          response: { data: body, status: res.status },
        });
      }

      const response = { data: body, status: res.status };
      return mapResponse ? mapResponse(response) : response;
    } catch (error) {
      throw mapError ? mapError(error) : error;
    } finally {
      if (timer) clearTimeout(timer);
      controllers.delete(controller);
    }
  }

  const get = (endpoint, params, mapResponse, mapError) =>
    request({ method: API.METHOD.GET, url: endpoint, params, mapResponse, mapError });

  const post = (endpoint, body, mapResponse, mapError) =>
    request({ method: API.METHOD.POST, url: endpoint, data: body, mapResponse, mapError });

  const put = (endpoint, body, mapResponse, mapError) =>
    request({ method: API.METHOD.PUT, url: endpoint, data: body, mapResponse, mapError });

  const cancelAll = () => {
    controllers.forEach((controller) => controller.abort());
    controllers.clear();
  };

  return { get, post, put, cancelAll };
}

export { createApiInstance };
